import { MUICheckBox, MUIChip, MUIDateTimePicker } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import dayjs, { Dayjs } from "dayjs"
import { MouseEvent, useCallback, useContext, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { Navigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../../components/DiMaProgress"
import { UserContext } from "../../../../../context/UserContext"
import {
  ApplicationRoleUserGet,
  ApplicationRoleUserPut,
} from "../../../../../models/ApplicationRoleUser"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import {
  getLocalDateTime,
  getNiceFormattedDate,
  getUtcDateTime,
} from "../../../../../utils/getFormattedDate"
import { getUserApplicationStatusProps } from "../../../../../utils/getUserApplicationStatusProps"

const validationSchema = z.object({
  validityStartDate: z.string().min(1, "Validity Start Date is required"),
  validityEndDate: z.string().min(1, "Validity End Date is required"),
  isDisabled: z.boolean({ error: "Is Disabled is required" }),
})

const MAX_DATE = dayjs("9999-12-31T23:59:59")

export function UserApplicationRoleDetails(props: {
  readonly userApplicationRole: ApplicationRoleUserGet
  readonly setUserApplicationRole: (
    updatedUserApplicationRole: ApplicationRoleUserGet
  ) => void
}) {
  const { userApplicationRole, setUserApplicationRole } = props
  const { userId, applicationId, roleId } = useParams()
  const { user, isLoadingUser } = useContext(UserContext)
  const returnURL = `/users/${userId}#apps-roles`

  const formMethods = useForm<ApplicationRoleUserPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = formMethods

  const validityStartDate = useWatch({
    control,
    name: "validityStartDate",
  })

  useEffect(() => {
    reset({
      validityStartDate: getLocalDateTime(userApplicationRole.validityStartDate),
      validityEndDate: getLocalDateTime(userApplicationRole.validityEndDate),
      isDisabled: userApplicationRole.isDisabled || false,
    })
  }, [userApplicationRole, reset])

  const onSubmit = useCallback(
    async (values: ApplicationRoleUserPut) => {
      const utcValidityStartDate = getUtcDateTime(values.validityStartDate)
      const utcValidityEndDate = getUtcDateTime(values.validityEndDate)

      try {
        const res = await digitalManagerApi.put<ApplicationRoleUserGet>(
          `/users/${userId}/applications/${applicationId}/roles/${roleId}`,
          {
            validityStartDate: utcValidityStartDate,
            validityEndDate: utcValidityEndDate,
            isDisabled: values.isDisabled,
          }
        )
        toast.success("User application role updated")
        setUserApplicationRole(res.data)
      } catch {
        toast.error("Cannot update user application role")
      }
    },
    [applicationId, roleId, setUserApplicationRole, userId]
  )

  const handleDateChangeValidityStartDate = useCallback(
    (field: ControllerRenderProps<ApplicationRoleUserPut, "validityStartDate">) =>
      (date: Dayjs | null) => {
        field.onChange(date ? date.toISOString() : "")
      },
    []
  )

  const handleValidityStartDatePickerChange = useCallback(
    (field: ControllerRenderProps<ApplicationRoleUserPut, "validityStartDate">) =>
      (date: Dayjs | null) => {
        if (date?.isAfter(MAX_DATE)) {
          setValue("validityStartDate", MAX_DATE.toISOString(), {
            shouldValidate: true,
          })
          return
        }
        handleDateChangeValidityStartDate(field)(date)
      },
    [handleDateChangeValidityStartDate, setValue]
  )

  const renderDateTimePickerValidityStartDate = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationRoleUserPut, "validityStartDate">
    }) => (
      <MUIDateTimePicker
        id="validityStartDate"
        name="validityStartDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityStartDatePickerChange(field)}
        errors={
          errors.validityStartDate
            ? { validityStartDate: { message: String(errors.validityStartDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityStartDate, handleValidityStartDatePickerChange]
  )

  const handleCheckboxToggle = useCallback(
    (field: ControllerRenderProps<ApplicationRoleUserPut, "isDisabled">) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckboxIsDisabled = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationRoleUserPut, "isDisabled">
    }) => (
      <MUICheckBox
        {...field}
        checked={field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  const handleDateChangeValidityEndDate = useCallback(
    (field: ControllerRenderProps<ApplicationRoleUserPut, "validityEndDate">) =>
      (date: Dayjs | null) => {
        field.onChange(date ? date.toISOString() : "")
      },
    []
  )

  const handleValidityEndDatePickerChange = useCallback(
    (field: ControllerRenderProps<ApplicationRoleUserPut, "validityEndDate">) =>
      (date: Dayjs | null) => {
        const minDate = validityStartDate ? dayjs(validityStartDate) : null

        if (date && minDate && date.isBefore(minDate)) {
          setValue("validityEndDate", "", { shouldValidate: true })
          return
        }

        if (date?.isAfter(MAX_DATE)) {
          setValue("validityEndDate", MAX_DATE.toISOString(), { shouldValidate: true })
          return
        }

        handleDateChangeValidityEndDate(field)(date)
      },
    [validityStartDate, setValue, handleDateChangeValidityEndDate]
  )

  const renderDateTimePickerValidityEndDate = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationRoleUserPut, "validityEndDate">
    }) => (
      <MUIDateTimePicker
        id="validityEndDate"
        name="validityEndDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityEndDatePickerChange(field)}
        errors={
          errors.validityEndDate
            ? { validityEndDate: { message: String(errors.validityEndDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityEndDate, handleValidityEndDatePickerChange]
  )

  if (!userApplicationRole || !applicationId) {
    return <Navigate to={returnURL} />
  }

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const canWrite = hasUserAccessToApplicationFeature(
    user.authorizations.applications,
    applicationId,
    DimaFeatureShortNameEnum.RM,
    AccessLevel.Write
  )

  const detailsContentProps: DiMaDetailsContentProps<ApplicationRoleUserPut> = {
    canWrite,
    formMethods,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Status",
        displayItem: (
          <MUIChip {...getUserApplicationStatusProps(userApplicationRole.status)} />
        ),
      },
      {
        id: 1,
        label: "Validity Start Date",
        displayItem: (
          <div>{getNiceFormattedDate(userApplicationRole.validityStartDate)}</div>
        ),
        editItem: (
          <Controller
            name="validityStartDate"
            control={control}
            render={renderDateTimePickerValidityStartDate}
          />
        ),
      },
      {
        id: 2,
        label: "Validity End Date",
        displayItem: (
          <div>{getNiceFormattedDate(userApplicationRole.validityEndDate)}</div>
        ),
        editItem: (
          <Controller
            name="validityEndDate"
            control={control}
            render={renderDateTimePickerValidityEndDate}
          />
        ),
      },
      {
        id: 3,
        label: "Is Disabled",
        displayItem: <div>{userApplicationRole.isDisabled?.toString() || "false"}</div>,
        editItem: (
          <Controller
            name="isDisabled"
            control={control}
            render={renderCheckboxIsDisabled}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
